import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Flex } from '@electricjs/arc';

import { ProgressStepper } from '@common';
import formatDate from '@/helpers/formatDate';

import { useStorefront } from '../StorefrontProvider';
import { useCurrentCatalogEstimatedDates } from '../utils/useCurrentCatalogEstimatedDates';

const StyledFlex = styled(Flex)`
  padding: 2rem;
  width: 100%;
`;

const OrderProgressStepper = () => {
  const { t } = useTranslation('storefront');
  const { storefrontBackorderPossibility } = useFlags();

  const { hasOnlyAssetsInCart, hasOnlyCatalogProductsInCart, hasMixedCart } =
    useStorefront();

  const catalogEstimatedDates = useCurrentCatalogEstimatedDates();

  const productsOnlyList = [
    {
      completed: true,
      title: t('orderProgress.orderPlaced'),
      subTitle: formatDate(new Date().toISOString()),
    },
    {
      completed: true,
      title: t('orderProgress.assetsCreatedAndAssigned'),
      subTitle: formatDate(new Date().toISOString()),
      wrapText: true,
    },
    {
      completed: false,
      title: t('orderProgress.purchaseShipped'),
      subTitle: storefrontBackorderPossibility
        ? ''
        : t('orderProgress.estimated', {
            date: formatDate(catalogEstimatedDates?.shipDate?.toISOString()),
          }),
    },
    {
      completed: false,
      title: t('orderProgress.deliveryReceived'),
      subTitle: storefrontBackorderPossibility
        ? ''
        : t('orderProgress.estimated', {
            date: formatDate(
              catalogEstimatedDates?.deliveryDate?.toISOString()
            ),
          }),
    },
  ];

  const assetsOnlyList = [
    {
      completed: true,
      title: t('orderProgress.orderPlaced'),
      subTitle: formatDate(new Date().toISOString()),
    },
    {
      completed: true,
      title: t('orderProgress.assetsAssigned'),
      subTitle: formatDate(new Date().toISOString()),
    },
    {
      completed: false,
      title: t('orderProgress.assetsRetrieved'),
    },
  ];

  const mixedCartList = [
    {
      completed: true,
      title: t('orderProgress.orderPlaced'),
      subTitle: formatDate(new Date().toISOString()),
    },
    {
      completed: true,
      title: t('orderProgress.assetsCreatedAndAssigned'),
      subTitle: formatDate(new Date().toISOString()),
      wrapText: true,
    },
    {
      completed: false,
      title: t('orderProgress.assetsRetrieved'),
    },
    {
      completed: false,
      title: t('orderProgress.purchaseShipped'),
      subTitle: storefrontBackorderPossibility
        ? ''
        : t('orderProgress.estimated', {
            date: formatDate(catalogEstimatedDates?.shipDate?.toISOString()),
          }),
    },
    {
      completed: false,
      title: t('orderProgress.deliveryReceived'),
      subTitle: storefrontBackorderPossibility
        ? ''
        : t('orderProgress.estimated', {
            date: formatDate(
              catalogEstimatedDates?.deliveryDate?.toISOString()
            ),
          }),
    },
  ];

  // Assets only cart
  if (hasOnlyAssetsInCart) {
    return (
      <StyledFlex>
        <ProgressStepper stepperList={assetsOnlyList} />
      </StyledFlex>
    );
  }

  // Catalog only cart
  if (hasOnlyCatalogProductsInCart) {
    return (
      <StyledFlex>
        <ProgressStepper stepperList={productsOnlyList} />
      </StyledFlex>
    );
  }

  // Mixed cart (catalog + assets)
  if (hasMixedCart) {
    return (
      <StyledFlex>
        <ProgressStepper stepperList={mixedCartList} />
      </StyledFlex>
    );
  }

  return null;
};

export default OrderProgressStepper;
